import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { store } from "store";
import { hideAllMenus, toggleProfileMenu } from "store/application/application-action";
import { HomeButton, ExploreButton, ProfileButton, SavedButton, UploadButton } from "components/footer";
import { routes } from "routes";

import "./ApplicationFooter.scss";
import { metrics } from "utils/metrics";
import { useMeQuery } from "queries";
import { fetchCurrentUserSuccess } from "store/user/user-action";

enum TABS {
  home = "/",
  explore = "/explore",
  add = "/create",
  saved = "/saved",
  profile = "/profile",
}

const isHomeTabOpen = (path: string): boolean => {
  return path === routes.home();
};

const isExploreTabOpen = (path: string): boolean => {
  return [
    routes.explore(),
    routes.exploreCreators(),
    routes.exploreImages(),
    routes.niches(),
    routes.exploreTop(),
  ].includes(path);
};

const isCreateTabOpen = (path: string): boolean => {
  return path === routes.create();
};

const isSavedTabOpen = (path: string, username: string|null): boolean => {
  if (username === null) {
    return false;
  }

  if (path === routes.userCollections(username)) {
    return true;
  }

  if (path.indexOf(`${routes.userCollections(username)}/`) === 0) {
    return true;
  }

  return false;
};

const isProfileTabOpen = (path: string, username: string|null): boolean => {
  if (username === null) {
    return false;
  }

  return path === routes.user(username);
};

const ApplicationFooter = () => {
  const [lastTab, setLastTab] = useState<string|null>(null);

  const me = useMeQuery();

  const currentUserName = me.data ? me.data.username : null;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const location = useLocation();

  const getCurrentTab = (): string|null => {
    const path = location.pathname;

    if (isProfileTabOpen(path, currentUserName) || lastTab === TABS.profile) {
      return TABS.profile;
    } else if (isHomeTabOpen(path)) {
      return TABS.home;
    } else if (isExploreTabOpen(path)) {
      return TABS.explore;
    } else if (isCreateTabOpen(path)) {
      return TABS.add;
    } else if (isSavedTabOpen(path, currentUserName)) {
      return TABS.saved;
    } else {
      return null;
    }
  };

  const handleTabClick = (tabName: TABS) => async () => {
    if (tabName === lastTab) {
      setLastTab(null);
    } else {
      setLastTab(tabName);
    }

    if (tabName === TABS.profile) {
      store.dispatch(toggleProfileMenu());
    } else if (tabName === TABS.saved && currentUserName) {
      store.dispatch(hideAllMenus());
      navigate(routes.userCollections(currentUserName));
    } else {
      navigate(tabName);
      store.dispatch(hideAllMenus());
      window.scrollTo({ top: 0 });
    }
    await metrics.sendMenuButtonClicked({
      name: tabName
    });
  };

  useEffect(() => {
    if (me.data) {
      dispatch(fetchCurrentUserSuccess(me.data));
    }
    // eslint-disable-next-line
  }, [me]);

  return (
    <div className="appFooter">
      <HomeButton
        active={getCurrentTab() === TABS.home}
        onClick={handleTabClick(TABS.home)}
      />

      <ExploreButton
        active={getCurrentTab() === TABS.explore}
        onClick={handleTabClick(TABS.explore)}
      />

      <UploadButton
        active={getCurrentTab() === TABS.add}
        onClick={handleTabClick(TABS.add)}
      />

      <SavedButton
        active={getCurrentTab() === TABS.saved}
        username={currentUserName}
        onClick={handleTabClick(TABS.saved)}
      />

      <ProfileButton
        active={getCurrentTab() === TABS.profile}
        username={currentUserName}
        onClick={handleTabClick(TABS.profile)}
      />
    </div>
  );
};

export default ApplicationFooter;
